/**
 * Gli avvisi automatici: a che punto sono e come provarli subito.
 *
 * È la sezione «Server di posta» vista dall'altro capo: quella configura da
 * dove parte la posta, questa dice quando parte.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErroreApi } from '../../api/client.ts';
import { notifiche as apiNotifiche } from '../../api/scadenziario.ts';
import type { EsitoNotifiche, VoceNotifica } from '../../api/tipi-dati.ts';
import { dataBreve, istante } from '../../componenti/formato.ts';
import { useConfigurazioneSmtp } from './configurazione.ts';

export function AvvisiAutomatici() {
  const { config, errore: erroreConfig } = useConfigurazioneSmtp();

  const [voci, setVoci] = useState<VoceNotifica[]>([]);
  const [totale, setTotale] = useState(0);
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<EsitoNotifiche | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [ricarica, setRicarica] = useState(0);

  useEffect(() => {
    let annullato = false;

    apiNotifiche
      .storico(15)
      .then((r) => { if (!annullato) { setVoci(r.elementi); setTotale(r.totale); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      });

    return () => { annullato = true; };
  }, [ricarica]);

  async function esegui() {
    setErrore(null);
    setEsito(null);
    setInCorso(true);

    try {
      const r = await apiNotifiche.esegui();
      setEsito(r);
      setRicarica((n) => n + 1);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInCorso(false);
    }
  }

  // Finché la configurazione non è arrivata si tiene il pulsante spento: è
  // meno peggio di offrirlo e scoprire poi che l'invio non era attivo.
  const attivo = config?.attivo ?? false;

  return (
    <div className="pannello pannello-imbottito">
      <h2 className="sezione-titolo">Avvisi automatici</h2>
      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 12 }}>
        Una volta al giorno lo scadenziario avvisa chi di dovere. Ogni scadenza produce fino
        a <strong>tre avvisi</strong> mentre la data si avvicina, più uno quando la data è
        passata ed è ancora aperta. Ciascuno parte una volta sola — se ripartisse ogni
        mattina, in due settimane nessuno leggerebbe più quelle email.
      </p>

      <ul className="elenco-stadi">
        <li>
          <span aria-hidden="true">◔</span>{' '}
          <strong>Primo avviso</strong> — quando la scadenza entra nel periodo di preavviso
          configurato sul suo tipo.
        </li>
        <li>
          <span aria-hidden="true">◑</span>{' '}
          <strong>Secondo avviso</strong> — a metà di quel periodo, se è ancora aperta.
        </li>
        <li>
          <span aria-hidden="true">◕</span>{' '}
          <strong>Ultima chiamata</strong> — a un quarto del periodo, e comunque mai più di
          sette giorni prima: su un preavviso di sei mesi, un&rsquo;ultima chiamata a un mese
          e mezzo dalla data non servirebbe a niente.
        </li>
        <li>
          <span aria-hidden="true">⚠</span>{' '}
          <strong>Scaduta</strong> — la data è passata e la scadenza è ancora aperta.
        </li>
      </ul>

      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 16 }}>
        Con un preavviso corto gli avvisi diventano due o uno: tre email in tre giorni sono
        rumore, non un promemoria. Il numero esatto si vede mentre lo si imposta, nel form
        del tipo di scadenza. Il <strong>lunedì</strong>, in coda all&rsquo;email del giorno,
        arriva anche il quadro di tutto quello che è aperto e vicino alla scadenza: copre il
        tempo che passa fra un avviso e il successivo quando il preavviso è lungo.
      </p>

      {erroreConfig !== null && <div className="avviso-errore" role="alert">{erroreConfig}</div>}
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      {esito !== null && (
        <div className={esito.eseguita ? 'avviso-esito' : 'avviso-attenzione'} role="status">
          {esito.eseguita ? (
            <>
              <strong>
                {esito.emailInviate === 0
                  ? 'Nessuna email da mandare.'
                  : `${esito.emailInviate} ${esito.emailInviate === 1 ? 'email inviata' : 'email inviate'}.`}
              </strong>{' '}
              {esito.emailInviate === 0
                ? 'Tutto ciò che c\'era da segnalare era già stato segnalato.'
                : `Scadenze segnalate: ${esito.preavvisi} in preavviso, ${esito.scadute} oltre la data.`}
              {esito.riepiloghi > 0 &&
                ` Il quadro della settimana è partito verso ${esito.riepiloghi} ${esito.riepiloghi === 1 ? 'destinatario' : 'destinatari'}.`}
              {esito.emailFallite > 0 && ` ${esito.emailFallite} non è arrivata: ${esito.errori.join('; ')}`}
            </>
          ) : (
            esito.motivo
          )}
        </div>
      )}

      <div className="campo-affiancato">
        <button type="button" className="bottone" onClick={() => void esegui()} disabled={inCorso || !attivo}>
          {inCorso ? 'Invio in corso…' : 'Esegui adesso'}
        </button>
        <span className="aiuto">
          {attivo ? (
            'Fa subito quello che l\'applicazione fa da sola ogni giorno. Non genera doppioni.'
          ) : (
            // Dire solo «disponibile quando l'invio è attivo» lascia il
            // problema senza indirizzo: la cosa da fare sta in un'altra
            // sezione, e ci si porta con un clic.
            <>Disponibile quando l'invio è attivo: si accende da <Link to="/impostazioni/posta">Server di posta</Link>.</>
          )}
        </span>
      </div>

      <h3 className="sezione-titolo" style={{ marginTop: 24 }}>
        Ultimi avvisi mandati
        {totale > 0 && <span className="conteggio" style={{ marginLeft: 8 }}>{totale} in tutto</span>}
      </h3>

      {voci.length === 0 ? (
        <p className="stato-vuoto">Nessun avviso è ancora partito.</p>
      ) : (
        <div className="tabella-contenitore">
          <table>
            <thead>
              <tr>
                <th scope="col">Quando</th>
                <th scope="col">Tipo</th>
                <th scope="col">Scadenza</th>
                <th scope="col" className="col-espandi">Destinatario</th>
                <th scope="col">Esito</th>
              </tr>
            </thead>
            <tbody>
              {voci.map((v) => (
                <tr key={v.id}>
                  <td className="data">{istante(v.inviataIl)}</td>
                  <td className="tipo-nome">
                    <span aria-hidden="true">{v.tipo === 'SCADUTA' ? '⚠ ' : '◔ '}</span>
                    {v.tipo === 'SCADUTA' ? 'Scaduta' : 'Preavviso'}
                  </td>
                  <td>
                    <span className="soggetto-nome">{v.soggetto ?? '—'}</span>
                    {v.tipoScadenza !== null && <span className="tipo-nome"> · {v.tipoScadenza}</span>}
                    <span className="tenue"> · {dataBreve(v.giorno)}</span>
                  </td>
                  <td className="col-espandi">{v.destinatario}</td>
                  <td>
                    {v.esito === 'OK' ? (
                      <span className="esito">Consegnata</span>
                    ) : (
                      <>
                        <span className="badge badge-scaduta">
                          <span aria-hidden="true">✕</span> Non riuscita
                        </span>
                        {/* Il motivo va mostrato, non nascosto in un tooltip:
                            è l'unica informazione che permette di rimediare. */}
                        {v.errore !== null && <div className="esito">{v.errore}</div>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
